import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, Loader2, FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { DocumentDistributionPanel } from "@/components/workflows/DocumentDistributionPanel";

interface DistributeAndArchivePhaseProps {
  taskId: string;
  unaId: string;
  meetingMinutesId: string;
  task?: any;
  onPhaseComplete?: () => void;
}

export function DistributeAndArchivePhase({
  taskId,
  unaId,
  meetingMinutesId,
  task,
  onPhaseComplete,
}: DistributeAndArchivePhaseProps) {
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<any[]>([]);
  const [distributionComplete, setDistributionComplete] = useState(false);
  const [distributionRecipients, setDistributionRecipients] = useState<any[]>([]);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    loadDocuments();
    extractDistributionList();
    // Check if task is already completed
    if (task?.status === 'completed') {
      setIsCompleted(true);
    }
  }, [taskId, task?.status]);

  useEffect(() => {
    checkCompletionStatus();
  }, [documents]);

  function extractDistributionList() {
    const workflowData = task?.workflow_data || {};
    const distributionList = workflowData.distribution_list || [];
    
    if (!Array.isArray(distributionList)) {
      console.warn('distribution_list is not an array:', distributionList);
      setDistributionRecipients([]);
      return;
    }
    
    const recipients = distributionList.map((item: any) => ({
      id: `external_${item.email}`,
      name: item.name,
      email: item.email,
      type: 'external'
    }));
    
    setDistributionRecipients(recipients);
  }

  async function loadDocuments() {
    try {
      setLoading(true);
      
      // Load all documents for this task (not just finalized - includes 'final' status too)
      const { data, error } = await supabase
        .from('generated_documents')
        .select('*')
        .eq('task_id', taskId)
        .in('status', ['finalized', 'final'])
        .order('generated_at', { ascending: true });

      if (error) throw error;
      
      setDocuments(data || []);
    } catch (error: any) {
      console.error('Error loading documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }

  async function checkCompletionStatus() {
    await checkDistributionComplete();
  }

  async function checkDistributionComplete(): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('document_distribution_log')
        .select('id, status')
        .eq('task_id', taskId);

      if (error) throw error;

      const isComplete = data && data.length > 0 && data.every(d => d.status === 'sent');
      setDistributionComplete(isComplete);
      return isComplete;
    } catch (error) {
      console.error('Error checking distribution status:', error);
      return false;
    }
  }

  async function handleDistributionComplete() {
    await checkDistributionComplete();
  }

  async function handleMarkComplete() {
    try {
      setIsCompleting(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Use security definer function to bypass RLS recursion
      const { data: success, error: taskError } = await supabase
        .rpc('complete_task', {
          p_task_id: taskId,
          p_user_id: user.id
        });

      if (taskError) throw taskError;
      if (!success) throw new Error('Failed to complete task');

      // Update meeting_minutes status to 'finalized' (not 'archived') 
      // so it appears correctly in Meeting Records
      if (meetingMinutesId) {
        const { error: minutesError } = await supabase
          .from('meeting_minutes')
          .update({ status: 'finalized' })
          .eq('id', meetingMinutesId);

        if (minutesError) {
          console.error('Error updating meeting minutes status:', minutesError);
        }
      }

      setIsCompleted(true);
      toast.success('Meeting Minutes workflow completed successfully');
      onPhaseComplete?.();
    } catch (error: any) {
      console.error('Error completing workflow:', error);
      toast.error(error.message || 'Failed to complete workflow');
    } finally {
      setIsCompleting(false);
    }
  }

  // Completion requires distribution (documents are already linked to meeting_minutes via task_id)
  const allComplete = distributionComplete;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Show completion state if already completed
  if (isCompleted) {
    return (
      <div className="space-y-6">
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
              <div>
                <h3 className="font-semibold text-green-800">Meeting Minutes Workflow Completed</h3>
                <p className="text-sm text-green-700">
                  Documents have been distributed and are available in Meeting Records.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Phase Header */}
      <Card>
        <CardHeader>
          <CardTitle>Phase 3: Distribute & Complete</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <p className="text-muted-foreground">
              Final phase: distribute meeting minutes to stakeholders.
            </p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Distribute finalized minutes to UNA members and distribution list</li>
              <li>Documents are automatically saved to Meeting Records</li>
              <li>Complete meeting minutes workflow</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Status Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Phase 3 Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between p-2 border rounded">
            <span>Document Distribution</span>
            {distributionComplete ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <span className="text-sm text-muted-foreground">Pending</span>
            )}
          </div>
          <div className="flex items-center justify-between p-2 border rounded bg-muted/30">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span>Meeting Records Filing</span>
            </div>
            <CheckCircle2 className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-xs text-muted-foreground italic">
            Documents are automatically linked to Meeting Records via this workflow.
          </p>
        </CardContent>
      </Card>

      {/* Document Distribution */}
      <DocumentDistributionPanel
        taskId={taskId}
        unaId={unaId}
        documents={documents}
        additionalRecipients={distributionRecipients}
        onDistributionComplete={handleDistributionComplete}
      />

      {/* Complete Phase - Always show, but disable when not ready */}
      <Alert className={!allComplete ? 'border-muted' : 'border-green-200 bg-green-50'}>
        <AlertDescription>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">
                {allComplete ? 'Distribution Complete' : 'Complete Meeting Minutes Workflow'}
              </p>
              <p className="text-sm text-muted-foreground">
                {allComplete 
                  ? 'Minutes distributed. Ready to complete workflow.'
                  : 'Distribute documents to complete this workflow.'
                }
              </p>
            </div>
            <Button 
              onClick={handleMarkComplete} 
              disabled={!allComplete || isCompleting}
            >
              {isCompleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Completing...
                </>
              ) : (
                'Complete Workflow'
              )}
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}
